/**
 * Verse-number severity rings — mobile-parity indicator marking verses
 * with a KJV-vs-NASB problem (score 1-3). Order-only verses get nothing.
 *
 * Reader HTML is pre-generated static fragments, so rings are applied at
 * runtime after a chapter mounts, from the chapter-batch analysis
 * endpoint (localStorage-cached, so repeat chapters decorate instantly).
 */

import type { BibleLocation } from '@skjv/common';
import { fetchChapterAnalysis } from '@/api/verse-analysis';

const RING_CLASSES = ['has-analysis', 'analysis-severity-1', 'analysis-severity-2', 'analysis-severity-3'];

/** Fetch the chapter's problem scores and ring its verse numbers */
export function addAnalysisIndicators(chapterElement: HTMLElement, location: BibleLocation): void {
  void fetchChapterAnalysis(location).then((chapter) => {
    if (!chapter || chapter.verses.length === 0) return;
    // The chapter element may have been evicted by the seamless scroller
    // while the (first, uncached) fetch was in flight
    if (!chapterElement.isConnected) return;

    const { testamentIndex: t, bookIndex: b, chapterIndex: c } = location;
    for (const { verse, score } of chapter.verses) {
      if (score < 1) continue;
      const clamped = Math.min(3, Math.round(score));
      const verseNum = chapterElement.querySelector(
        `#v-${t}-${b}-${c}-${verse - 1} .verse-num`,
      );
      verseNum?.classList.add('has-analysis', `analysis-severity-${clamped}`);
    }
  });
}

/** Re-apply rings across all loaded chapters (idempotent per verse) */
export function refreshAnalysisIndicators(
  container: HTMLElement,
  loadedChapters: Map<string, HTMLElement>,
  parseChapterId: (id: string) => BibleLocation | null,
): void {
  container.querySelectorAll('.verse-num.has-analysis').forEach((el) => {
    el.classList.remove(...RING_CLASSES);
  });
  for (const [chapterId, chapterElement] of loadedChapters) {
    const location = parseChapterId(chapterId);
    if (location) addAnalysisIndicators(chapterElement, location);
  }
}
